import type { IBasePathAnalyticPageWithParent } from '../types';
import type { IPageInfo } from '~/modules/page-analytic/types';
import type { TemplateType } from '~/types/template';

import { useGetPagesInfoByIdsQuery } from '~/api/shopify/queries/pageInformation.generated';
import { getPageTitle, getShopifyPageId } from '~/modules/page-analytic/helpers';
import { useFeatureFlagChecker } from '~/modules/apps';
import { usePathAnalysisStore } from '../stores';
import { useMemo } from 'react';

interface IFetchShopifyPageInfoProps {
  pathAnalytics: IBasePathAnalyticPageWithParent[];
}
export const useFetchShopifyPageInfo = ({ pathAnalytics }: IFetchShopifyPageInfoProps) => {
  const pagesInfo = usePathAnalysisStore((state) => state.pagesInfo);
  const upsertPagesInfo = usePathAnalysisStore((state) => state.upsertPagesInfo);
  const { isEnabledFilterByShopifyPageId } = useFeatureFlagChecker();

  const pageIds = useMemo(() => {
    if (!isEnabledFilterByShopifyPageId) return [];

    const result: string[] = [];
    const uniquePathIds = new Set<string>();

    pathAnalytics.forEach((item) => {
      if (uniquePathIds.has(item.id) || !item.shopifyPageID) return;

      uniquePathIds.add(item.id);
      const pageId = getShopifyPageId({
        type: item.pageType,
        id: item.shopifyPageID,
      });

      if (pageId) {
        result.push(pageId);
      }
    });

    return result;
  }, [pathAnalytics, isEnabledFilterByShopifyPageId]);

  const { isFetching } = useGetPagesInfoByIdsQuery(
    {
      ids: pageIds,
    },
    {
      enabled: pageIds.length > 0,
      onSuccess: (data) => {
        if (data.nodes.length === 0) {
          return;
        }
        const newPageInfo = data.nodes.reduce<IPageInfo>((acc, item) => {
          if (item && 'id' in item && item.id) {
            // only get numeric id
            const numericId = item.id.split('/').pop() || item.id;

            acc[numericId] = item;
          }
          return acc;
        }, {});
        upsertPagesInfo(newPageInfo);
      },
    },
  );

  const pathAnalyticsWithPageInfo = useMemo(() => {
    return pathAnalytics.map((item) => {
      if (!item.shopifyPageID || !isEnabledFilterByShopifyPageId) return item;

      const pageInfo = pagesInfo[item.shopifyPageID];
      const pageTitle = getPageTitle({
        shopifyPageType: item.pageType as TemplateType,
        pageInfo,
      });

      return {
        ...item,
        pageTitle,
      };
    });
  }, [pagesInfo, pathAnalytics, isEnabledFilterByShopifyPageId]);

  return {
    isFetchPageInfoLoading: isFetching,
    pathAnalyticsWithPageInfo,
  };
};
